import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner } from '@w7/ui'
import {
  claims,
  consumeKcActionResult,
  consumeNext,
  handleOidcCallback,
  hasConsoleAccess,
  resolveConsoleHierarchyGate,
} from '@/auth/oidc'
import { flashToast, toastCopy } from '@/ui/toaster'
import './auth.css'

export function CallbackPage() {
  const navigate = useNavigate()
  const [message] = useState('Signing you in…')

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const callbackUrl = new URL(location.href)
      const aia = consumeKcActionResult(callbackUrl)
      const result = await handleOidcCallback()
      if (cancelled) return
      history.replaceState({}, '', '/callback')

      if (!result.ok) {
        const reason = result.reason === 'missing-code' ? 'denied' : 'error'
        flashToast(reason === 'denied' ? toastCopy.loginDenied : toastCopy.loginError)
        navigate(`/login?reason=${reason}`, { replace: true })
        return
      }

      const who = claims()
      if (!hasConsoleAccess(who)) {
        const cm = who?.hierarchyLevel === '1'
        navigate(cm ? '/no-access?reason=company-master' : '/no-access', { replace: true })
        return
      }
      // Existing Company Masters may still have `agent` without hierarchyLevel claim.
      if (who?.agentId && !who.hierarchyLevel) {
        const gate = await resolveConsoleHierarchyGate(who.agentId)
        if (gate === 'denied') {
          navigate('/no-access?reason=company-master', { replace: true })
          return
        }
      }

      // Application-initiated actions (password / MFA) — toast from real Keycloak status.
      if (aia.action === 'UPDATE_PASSWORD') {
        if (aia.status === 'success') flashToast(toastCopy.passwordChanged)
        else if (aia.status === 'cancelled') flashToast(toastCopy.passwordCancelled)
        else flashToast(toastCopy.passwordChangeIncomplete)
        navigate(consumeNext('/settings/security'), { replace: true })
        return
      }

      if (aia.action) {
        const next = consumeNext('/settings/security')
        const isDelete = aia.action.startsWith('delete_credential:')
        const isPasskeyEnroll =
          aia.action === 'webauthn-register' || aia.action.includes('webauthn')
        // Cancelled → stay on; success → ?removed=1 / ?enrolled= toast.
        // Missing kc_action_status (null) is common on some realms — verify on the hub
        // instead of falsely erroring (“Could not remove factor”).
        if (aia.status === 'cancelled') {
          flashToast(
            isDelete
              ? {
                  tone: 'info',
                  message: 'Removal cancelled',
                  detail: 'Your second factor is unchanged.',
                }
              : {
                  tone: 'info',
                  message: isPasskeyEnroll ? 'Passkey setup cancelled' : 'Setup cancelled',
                  detail: 'Your second factors are unchanged.',
                },
          )
          navigate('/settings/security', { replace: true })
          return
        }
        if (aia.status === 'error') {
          if (isDelete) {
            flashToast({
              tone: 'error',
              message: 'Could not remove factor',
              detail: 'Keycloak rejected the delete. Try again from Security.',
            })
          } else if (isPasskeyEnroll) {
            // KC 26: often “Device already exists with the same name” (label clash)
            // or a passkey is stored but Account REST did not list it yet.
            flashToast({
              tone: 'warn',
              message: 'Passkey not registered',
              detail:
                'Keycloak rejected setup — usually a duplicate device name, or a passkey already on the account. Refresh Security; if Passkey stays Available, Try again with a unique name.',
            })
            navigate('/settings/security?passkey_conflict=1', { replace: true })
            return
          } else {
            flashToast(toastCopy.mfaEnrollError('Keycloak rejected setup. Try again from Security.'))
          }
          navigate('/settings/security', { replace: true })
          return
        }
        if (isDelete && aia.status === null) {
          navigate('/settings/security?removed=verify', { replace: true })
          return
        }
        navigate(next, { replace: true })
        return
      }

      flashToast(toastCopy.loginSuccess)
      navigate(consumeNext('/overview'), { replace: true })
    })().catch(() => {
      if (!cancelled) {
        flashToast(toastCopy.loginError)
        navigate('/login?reason=error', { replace: true })
      }
    })
    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <div className="w7-auth w7-auth-center" data-testid="auth-callback">
      <Spinner size={22} />
      <p className="w7-dim" style={{ marginTop: 16 }}>
        {message}
      </p>
    </div>
  )
}
